import { useCallback, useEffect, useState } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import { StockScannerEvent, StockScannerUniverse } from "../../../types";
import { StockScannerButtonGroup } from "./StockScannerButtonGroup";
import { StockScannerItem } from "./StockScannerItem";
import {
	stockScannerFundamentals,
	stockScannerPriceActions,
} from "./stockScannerConfig";

export const StockScanners = ({
	eventEmitter,
	onStateChange,
}: {
	eventEmitter?: EventEmitter;
	onStateChange?: OnStateChange;
}) => {
	const [priceActionEvent, setPriceActionEvent] = useState<StockScannerEvent>(
		StockScannerEvent.MOST_UP,
	);
	const [fundamentalEvent, setFundamentalEvent] = useState<StockScannerEvent>(
		StockScannerEvent.HIGH_GROWTH,
	);
	const [momentumEvent, setMomentumEvent] = useState<StockScannerEvent>(
		StockScannerEvent.TIMING_MOMENTUM,
	);
	const [driftEvent, setDriftEvent] = useState<StockScannerEvent>(
		StockScannerEvent.UPDRIFT,
	);

	const handler = useCallback(
		({
			section,
			eventValue,
		}: {
			section: string;
			eventValue: StockScannerEvent;
		}) => {
			switch (section) {
				case "earnings-momentum":
				case "timing-momentum":
					setMomentumEvent(eventValue);
					break;
				case "stock-drift":
				case "powerdays":
					setDriftEvent(eventValue);
					break;
			}
		},
		[],
	);

	useEffect(() => {
		eventEmitter?.on("stockScannerNavigation", handler);

		return () => {
			eventEmitter?.off("stockScannerNavigation", handler);
		};
	}, [eventEmitter, handler]);

	const handlePriceActionClick = (eventValue: StockScannerEvent) => {
		setPriceActionEvent(eventValue);
	};

	const handleFundamentalClick = (eventValue: StockScannerEvent) => {
		setFundamentalEvent(eventValue);
	};

	return (
		<div
			className="w-full max-w-[1600px]"
			data-testid="stock-scanners-component"
		>
			<div className="grid gap-3 mb-5 grid-rows-[auto-auto] [@container(min-width:456px)]:grid-cols-[repeat(auto-fit,minmax(456px,1fr))] [@container(min-width:456px)]:grid-rows-[auto_auto_auto_auto] [@container(min-width:912px)]:grid-cols-[repeat(2,1fr)] [@container(min-width:912px)]:grid-rows-[auto_auto]">
				<StockScannerButtonGroup
					title="Fundamental"
					events={stockScannerFundamentals}
					activeEvent={fundamentalEvent}
					onButtonClick={handleFundamentalClick}
					className="[@container(min-width:912px)]:col-start-1"
				/>
				<StockScannerItem
					id="fundamental"
					universeValue={StockScannerUniverse.TOP_500}
					eventValue={fundamentalEvent}
					onEventChange={setFundamentalEvent}
					onStateChange={onStateChange}
					className="[@container(min-width:912px)]:col-start-1"
				/>
				<StockScannerButtonGroup
					title="Price Action"
					events={stockScannerPriceActions}
					activeEvent={priceActionEvent}
					onButtonClick={handlePriceActionClick}
					className="[@container(min-width:912px)]:col-start-2 [@container(min-width:912px)]:row-start-1"
				/>
				<StockScannerItem
					id="price-action"
					universeValue={StockScannerUniverse.TOP_500}
					eventValue={priceActionEvent}
					onEventChange={setPriceActionEvent}
					onStateChange={onStateChange}
					className="[@container(min-width:912px)]:col-start-2 [@container(min-width:912px)]:row-start-2"
				/>
			</div>
			<div className="flex flex-col gap-5">
				<StockScannerItem
					id="timing-momentum"
					universeValue={StockScannerUniverse.ALL_STOCKS}
					eventValue={momentumEvent}
					isAdvancedScanner={true}
					onEventChange={setMomentumEvent}
					onStateChange={onStateChange}
				/>
				<StockScannerItem
					id="stock-drift"
					universeValue={StockScannerUniverse.SMALL_MICRO_CAPS}
					eventValue={driftEvent}
					isAdvancedScanner={true}
					onEventChange={setDriftEvent}
					onStateChange={onStateChange}
				/>
			</div>
		</div>
	);
};
